/**
 * Who is changing what, right now.
 *
 * A modal behind a header button, the same shape as the room's status and for
 * the same reason: a band above the stream taxes every glance at the room, and
 * the room is the product. The button carries the signal — see
 * `claimsButtonState` — so an overlap is never hiding behind a closed door.
 *
 * **It is a tree, and that is the whole design.** A list of paths answers "who
 * holds what" and hides the only question worth asking, which is where two
 * branches meet: `web/src/room/Workspace.tsx` and `web/src/room` overlap, look
 * nothing alike, and do not even sort next to each other. On a tree they are one
 * row apart.
 *
 * Everything a reader sees here has to say what is actually true — separate
 * worktrees, nothing broken, a merge coming — because a panel that shouts about
 * a clash that is not happening is one people learn to close.
 */
import { useEffect, useRef } from 'react'
import type { Claim } from '../api/types'
import { Face } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { describeAge } from '../status/status'
import { type ClaimNode, claimTree, overlaps } from './claims'

/**
 * The rail down the left of a row: one hairline per level of ancestry, plus the
 * elbow into this row.
 *
 * Drawn rather than indented with padding, because indentation alone leaves a
 * reader counting pixels to work out what is inside what, and a path tree is
 * only worth having if the nesting is legible at a glance.
 *
 * **Structural, never coloured.** It briefly lit the branch above an overlap,
 * which reads well in a mockup and lies in a real repository: the same lit line
 * runs past every innocent sibling in the directory, and there is one accent
 * here precisely so it never comes to mean "near something". The rows that are
 * actually in the argument say so themselves.
 */
function Rail({ depth }: { depth: number }) {
  if (depth === 0) return null
  return (
    <span aria-hidden="true" style={{ display: 'flex', flex: 'none', alignSelf: 'stretch' }}>
      {Array.from({ length: depth - 1 }, (_, level) => (
        <span key={level} style={{ width: 15, flex: 'none', borderLeft: '1px solid var(--border)' }} />
      ))}
      <span style={{ width: 15, flex: 'none', position: 'relative', borderLeft: '1px solid var(--border)' }}>
        {/* The elbow. Half the row's height, so a run of siblings reads as one
            spine with rungs rather than as disconnected dashes. */}
        <span
          style={{
            position: 'absolute', left: 0, top: '50%', width: 9, height: 1,
            background: 'var(--border)',
          }}
        />
      </span>
    </span>
  )
}

/** Every claim at or under a node, one per agent — the shallowest each holds,
 *  because that is the claim that actually overlaps this row. */
function holdersUnder(node: ClaimNode): Claim[] {
  const found: Claim[] = []
  const seen = new Set<string>()
  const walk = (current: ClaimNode) => {
    for (const claim of current.holders) {
      if (seen.has(claim.agent)) continue
      seen.add(claim.agent)
      found.push(claim)
    }
    current.children.forEach(walk)
  }
  walk(node)
  return found
}

function Holders({ holders }: { holders: Claim[] }) {
  if (holders.length === 0) return null
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
      {holders.map((claim) => (
        <span
          key={`${claim.agent}:${claim.path}`}
          title={`${claim.agent} · ${describeAge(claim.claimed_at)}${claim.intent ? ` · ${claim.intent}` : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Face name={claim.agent} avatarKey={null} size={18} />
          <span
            style={{
              font: '400 var(--size-label)/1 var(--font-machine)',
              color: 'var(--text-meta)', whiteSpace: 'nowrap',
            }}
          >
            {describeAgeShort(claim.claimed_at)}
          </span>
        </span>
      ))}
    </span>
  )
}

/** "2m", "4h", "6d" — the age in the space a row end has. */
function describeAgeShort(when: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(when).getTime()) / 1000)
  if (Number.isNaN(seconds)) return ''
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}

function Row({ node }: { node: ClaimNode }) {
  const isBranch = node.children.length > 0
  return (
    <>
      <div
        style={{
          display: 'flex', alignItems: 'center', minHeight: 30,
          background: node.overlapping ? 'var(--accent-wash)' : 'transparent',
          borderRadius: node.overlapping ? radius.xs : undefined,
        }}
      >
        <Rail depth={node.depth} />
        <span
          style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            flex: 1, minWidth: 0, padding: '0 10px 0 6px',
          }}
        >
          <span
            title={node.path}
            style={{
              font: `${node.overlapping ? 700 : 400} 12.5px/1.5 var(--font-machine)`,
              // The other half of an overlap takes the accent without the wash:
              // it is in the argument, and it is not where the argument is
              // reported.
              color: node.overlapping
                ? 'var(--accent-bright)'
                : node.implicated ? 'var(--accent)'
                  : isBranch ? 'var(--text-dim)' : 'var(--text)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              // A directory reads as a directory. Cheaper than an icon and it
              // survives being copied out of the page as text.
              direction: 'ltr',
            }}
          >
            {node.label}{isBranch ? '/' : ''}
          </span>
          {node.overlapping && (
            <span
              style={{
                font: '700 9.5px/1 var(--font-interface)', letterSpacing: '.16em',
                textTransform: 'uppercase', color: 'var(--accent)', flex: 'none',
              }}
            >
              two branches
            </span>
          )}
        </span>
        {/* On an overlapping row, everyone involved — including the agent whose
            claim is on a row below. That row is the one a reader stops at, and
            "these two, here" has to be readable without following the rail down
            to find the other face. */}
        <Holders holders={node.overlapping ? holdersUnder(node) : node.holders} />
      </div>
      {node.children.map((child) => (
        <Row key={child.path} node={child} />
      ))}
    </>
  )
}

export function ClaimsModal({
  claims,
  roomName,
  onClose,
}: {
  claims: Claim[]
  roomName: string
  onClose: () => void
}) {
  const shell = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const tree = claimTree(claims)
  const heading = overlaps(tree)
  const agents = [...new Set(claims.map((claim) => claim.agent))].sort()

  return (
    <div
      role="dialog"
      aria-label="Who is changing what"
      onMouseDown={(event) => {
        if (event.target === shell.current) onClose()
      }}
      ref={shell}
      style={{
        position: 'absolute', inset: 0, zIndex: 40,
        display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,.62)',
      }}
    >
      <div
        style={{
          width: 'min(640px, calc(100% - 32px))',
          maxHeight: 'calc(100% - 56px)',
          marginBottom: 'var(--safe-bottom)',
          display: 'flex', flexDirection: 'column',
          background: 'var(--ground-raised)',
          border: '1px solid var(--border-strong)',
          borderRadius: radius.lg,
          boxShadow: 'var(--shadow-overlay)',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            display: 'flex', alignItems: 'baseline', gap: 12,
            padding: '16px 20px 14px', borderBottom: '1px solid var(--border)',
            flex: 'none',
          }}
        >
          <h2 style={{ margin: 0, font: '700 13px var(--font-interface)', letterSpacing: '-.01em', color: 'var(--text)' }}>
            Held right now
          </h2>
          <span
            title={roomName}
            style={{
              font: '400 var(--size-meta)/1 var(--font-machine)', color: 'var(--text-meta)',
              flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {roomName}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label="Close"
            className="sp-tap"
            style={{
              flex: 'none', cursor: 'pointer', background: 'none', border: 'none',
              font: '400 var(--size-label)/1 var(--font-machine)', color: 'var(--text-meta)',
            }}
          >
            ESC
          </button>
        </header>

        <div style={{ overflow: 'auto', padding: '14px 18px 18px', minHeight: 0 }}>
          {claims.length === 0 ? (
            <>
              <p style={{ margin: '4px 0 10px', fontFamily: 'var(--font-human)', fontStyle: 'italic', fontSize: 13.5, color: 'var(--text-dim)' }}>
                Nobody is holding anything here.
              </p>
              <p style={{ margin: 0, font: '400 11.5px/1.6 var(--font-machine)', color: 'var(--text-faint)' }}>
                Every agent works in its own worktree, so two of them rewriting
                one file costs nothing at all until the first one merges — and
                then it lands on the second, hours after the decision that caused
                it. An agent says what it is about to touch with{' '}
                <span style={{ color: 'var(--text-dim)' }}>stagepost claim &lt;path&gt;…</span>{' '}
                and finds out then, while it is still a conversation.
              </p>
            </>
          ) : (
            <>
              {/* The answer before the evidence. A tree is worth reading; a
                  sentence saying whether you need to is worth reading first. */}
              <p
                style={{
                  margin: '0 0 14px', font: '400 12.5px/1.55 var(--font-human)',
                  color: heading.length ? 'var(--accent-bright)' : 'var(--text-dim)',
                }}
              >
                {heading.length === 0
                  ? `${agents.length} agent${agents.length === 1 ? '' : 's'} holding ${claims.length} path${claims.length === 1 ? '' : 's'}, none of them the same work. Every branch here still merges clean.`
                  : `${heading.length} overlap${heading.length === 1 ? '' : 's'}. Separate worktrees, so nothing is broken — but these branches are heading for the same file, and whoever merges second inherits it.`}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {tree.map((node) => (
                  <Row key={node.path} node={node} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
